import { get } from "lodash-es";
import { memo } from "react";
import type { FieldPath, SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";

import type {
  UserRoleAddressManagementPermissionsRequest,
  UserRoleAssetsPermissionsRequest,
  UserRoleCommunityGroupPermissionsEntityRequest,
  UserRoleCommunityPermissionsRequest,
  UserRoleDocumentFolderPermissionsEntityRequest,
  UserRoleDocumentsPermissionsRequest,
  UserRoleEventsPermissionsRequest,
  UserRoleIntegrationsPermissionsRequest,
  UserRoleProjectManagementPermissionsRequest,
  UserRoleRepairCategoryPermissionsEntityRequest,
  UserRoleRepairManagementPermissionsRequest,
  UserRoleRequest,
  UserRoleServicePagesPermissionsRequest,
  UserRoleServicePartnerPagePermissionsEntityRequest,
  UserRoleSurveysPermissionsRequest,
  UserRoleUserManagementPermissionsRequest,
} from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormErrorWrapper } from "@/components/Form/FormErrorWrapper";
import { FormInput } from "@/components/Form/FormInput";
import { FormRadio, FormRadioGroup } from "@/components/Form/FormRadio";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PermissionListLayout, PermissionWrapper } from "@/components/Permission/Permission";
import { Wizard, WizardStep } from "@/components/Wizard/Wizard";
import { Accordion } from "@/design-system/Accordion";
import { FormField } from "@/design-system/FormField";
import { createRequiredStringRule } from "@/helpers/rules";
import { getRoleLabelByType } from "@/helpers/user-roles";
import { useSlug } from "@/hooks/useSlug";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import type { RoleWizardStep } from "./roleFormManager";

export interface PermissionLayoutStructure {
  id: string;
  groupLabel: string;
  items?: {
    label: string;
    modelPath: FieldPath<RoleFormFieldValues>;
    tooltip?: string;
  }[];
  subStructures?: PermissionLayoutStructure[];
}

export interface RoleFormProps {
  isDuplicate: boolean;
  onSubmit: SubmitHandler<RoleFormFieldValues>;
  defaultFormValues: RoleFormFieldValues;
  isSubmitting: boolean;
  structure: PermissionLayoutStructure[];
  initialStepId?: RoleWizardStep;
}

export function RoleForm(props: RoleFormProps): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();
  const formMethods = useForm<RoleFormFieldValues>({
    mode: "onChange",
    defaultValues: props.defaultFormValues,
  });

  const canLeave = async () => await formMethods.trigger();

  const isEditMode = !props.isDuplicate && props.defaultFormValues.name !== "";
  const title = isEditMode
    ? t("page.role.create-or-edit.edit-mode.title")
    : t("page.role.create-or-edit.create-mode.title");

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={title}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.role.list.title"),
              to: routes.roles.list({ slug }),
            },
            {
              name: title,
            },
          ]}
        />
      }
    >
      <Form.Root formMethods={formMethods} onSubmit={props.onSubmit}>
        <Form.Container>
          <Wizard
            actionsText={{
              finish: isEditMode
                ? t("component.wizard.action.edit.finish")
                : t("component.wizard.action.create.finish"),
            }}
            onFinish={formMethods.handleSubmit(props.onSubmit)}
            isSubmitting={props.isSubmitting}
            strictOrder={!isEditMode}
            initialStepId={props.initialStepId}
          >
            <WizardStep id={1} canLeave={canLeave} hasFinish={isEditMode} title={t("component.wizard.step-general")}>
              <Form.Body>
                <FormField label={t("model.role.name")} required>
                  <FormInput<RoleFormFieldValues>
                    data-testid="role-name"
                    name="name"
                    rules={{
                      validate: {
                        required: createRequiredStringRule(t, "model.role.name"),
                      },
                    }}
                  />
                </FormField>
                <FormField label={t("model.role.type")}>
                  <FormErrorWrapper<RoleFormFieldValues> name="type">
                    <FormRadioGroup layout="vertical">
                      <FormRadio<RoleFormFieldValues>
                        name="type"
                        value="resident"
                        label={getRoleLabelByType(t, "resident")}
                        rules={{
                          required: {
                            message: t("components.form.error.required", {
                              inputName: t("model.role.type"),
                            }),
                            value: true,
                          },
                        }}
                      />
                      <FormRadio<RoleFormFieldValues>
                        name="type"
                        value="projectAdmin"
                        label={getRoleLabelByType(t, "projectAdmin")}
                        rules={{
                          required: {
                            message: t("components.form.error.required", {
                              inputName: t("model.role.type"),
                            }),
                            value: true,
                          },
                        }}
                      />
                      <FormRadio<RoleFormFieldValues>
                        name="type"
                        value="projectOwner"
                        label={getRoleLabelByType(t, "projectOwner")}
                        rules={{
                          required: {
                            message: t("components.form.error.required", {
                              inputName: t("model.role.type"),
                            }),
                            value: true,
                          },
                        }}
                      />
                      <FormRadio<RoleFormFieldValues>
                        name="type"
                        value="maintenance"
                        label={t("model.role.type.maintenance")}
                        rules={{
                          required: {
                            message: t("components.form.error.required", {
                              inputName: t("model.role.type"),
                            }),
                            value: true,
                          },
                        }}
                      />
                      <FormRadio<RoleFormFieldValues>
                        name="type"
                        value="caretaker"
                        label={getRoleLabelByType(t, "caretaker")}
                        rules={{
                          required: {
                            message: t("components.form.error.required", {
                              inputName: t("model.role.type"),
                            }),
                            value: true,
                          },
                        }}
                      />
                      <FormRadio<RoleFormFieldValues>
                        name="type"
                        value="readOnly"
                        label={getRoleLabelByType(t, "readOnly")}
                        rules={{
                          required: {
                            message: t("components.form.error.required", {
                              inputName: t("model.role.type"),
                            }),
                            value: true,
                          },
                        }}
                      />
                      <FormRadio<RoleFormFieldValues>
                        name="type"
                        value="servicePartner"
                        label={getRoleLabelByType(t, "servicePartner")}
                        rules={{
                          required: {
                            message: t("components.form.error.required", {
                              inputName: t("model.role.type"),
                            }),
                            value: true,
                          },
                        }}
                      />
                    </FormRadioGroup>
                  </FormErrorWrapper>
                </FormField>
              </Form.Body>
            </WizardStep>
            <WizardStep
              id={2}
              canLeave={canLeave}
              hasFinish={isEditMode}
              title={t("component.wizard.step-permissions")}
            >
              <Form.Body>
                <MemoPermissionLayout structure={props.structure} defaultValues={props.defaultFormValues} />
              </Form.Body>
            </WizardStep>
          </Wizard>
        </Form.Container>
      </Form.Root>
    </DocumentPage>
  );
}

export interface RoleFormFieldValues {
  name: string;
  type: UserRoleRequest["type"];
  permissions: {
    community: UserRoleCommunityPermissionsRequest;
    communityGroups: Record<string, UserRoleCommunityGroupPermissionsEntityRequest>;
    repairManagement: UserRoleRepairManagementPermissionsRequest;
    repairCategories: Record<string, UserRoleRepairCategoryPermissionsEntityRequest>;
    folders: Record<string, UserRoleDocumentFolderPermissionsEntityRequest>;
    userManagement: UserRoleUserManagementPermissionsRequest;
    addressManagement: UserRoleAddressManagementPermissionsRequest;
    servicePartners: UserRoleServicePagesPermissionsRequest;
    servicePages: Record<string, UserRoleServicePartnerPagePermissionsEntityRequest>;
    surveys: UserRoleSurveysPermissionsRequest;
    events: UserRoleEventsPermissionsRequest;
    assets: UserRoleAssetsPermissionsRequest;
    projectManagement: UserRoleProjectManagementPermissionsRequest;
    documents: UserRoleDocumentsPermissionsRequest;
    integrations: UserRoleIntegrationsPermissionsRequest;
  };
}

interface PermissionLayoutProps {
  structure: PermissionLayoutStructure[];
  defaultValues: RoleFormFieldValues;
  lvl?: number;
}

function PermissionLayout({ structure, defaultValues, lvl = 0 }: PermissionLayoutProps): React.ReactNode {
  return (
    <Accordion.Root>
      {structure.map((permissionGroup) => (
        <Accordion.Item key={permissionGroup.id} value={permissionGroup.id}>
          <Accordion.Trigger isNested={lvl > 0}>{permissionGroup.groupLabel}</Accordion.Trigger>
          <Accordion.Content>
            {permissionGroup.items && (
              <PermissionListLayout>
                {permissionGroup.items.map((permission) => (
                  <PermissionWrapper key={permission.modelPath} layout="horizontal">
                    <FormCheckbox<RoleFormFieldValues>
                      alignTop
                      memorise={get(defaultValues, permission.modelPath) as boolean}
                      name={permission.modelPath}
                      label={permission.label}
                      tooltip={permission.tooltip}
                    />
                  </PermissionWrapper>
                ))}
              </PermissionListLayout>
            )}
            {permissionGroup.subStructures?.map((s) => (
              <PermissionLayout key={s.id} structure={[s]} defaultValues={defaultValues} lvl={lvl + 1} />
            ))}
          </Accordion.Content>
        </Accordion.Item>
      ))}
    </Accordion.Root>
  );
}

const MemoPermissionLayout = memo(PermissionLayout);
